import { format, isValid, parseISO } from "date-fns";

export type YardiJobRow = {
  job_code: string;
  status: string;
  error_message?: string | null;
  modified_at?: string | null;
};

export type YardiJobDisplay = {
  label: string;
  severity: "info" | "success" | "warning" | "error";
  jobCode?: string;
  lastAttempt?: string;
  message?: string;
  canResend: boolean;
};

const NO_JOB: YardiJobDisplay = {
  label: "No Yardi job for this deal",
  severity: "info",
  canResend: false,
};

// Yardi's text follows "(404): " in the stored message; only the readable part is shown to users
const readableMessage = (message?: string | null): string | undefined => {
  const text = message?.replace(/^Yardi rejected the job \(\d+\): /, "").trim();
  return text || undefined;
};

// Yardi refuses every change to a job that has transactions, so sending again can never work
const isLockedByTransactions = (message?: string | null): boolean =>
  /already has transactions in yardi/i.test(message ?? "");

export const describeYardiJob = (
  job: YardiJobRow | null | undefined
): YardiJobDisplay => {
  if (!job) return NO_JOB;

  const modified = job.modified_at ? parseISO(job.modified_at) : undefined;
  const lastAttempt =
    modified && isValid(modified)
      ? format(modified, "dd/MM/yyyy HH:mm")
      : undefined;
  const base = { jobCode: job.job_code, lastAttempt, canResend: false };

  switch (job.status) {
    case "created":
      return { ...base, label: "Sent to Yardi", severity: "success" };
    case "failed":
      if (isLockedByTransactions(job.error_message)) {
        return {
          ...base,
          label: "Yardi will not change this job",
          severity: "error",
          message: readableMessage(job.error_message),
        };
      }
      return {
        ...base,
        label: "Yardi did not accept the job",
        severity: "error",
        canResend: true,
        message:
          readableMessage(job.error_message) ??
          "Yardi gave no reason. Please contact the developers.",
      };
    case "pending":
      return {
        ...base,
        label: "Waiting for Yardi",
        severity: "warning",
        message: job.error_message ?? undefined,
      };
    default:
      return NO_JOB;
  }
};
